import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import {
  MdDownload,
  MdPeople,
  MdErrorOutline,
  MdSearch,
  MdMoreVert,
  MdMarkEmailRead,
  MdFilterList,
  MdClose,
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
  MdEdit,
  MdArrowDownward,
  MdArrowUpward,
  MdCheckCircle,
  MdHighlightOff,
} from "react-icons/md";
import { HiOutlineTicket } from "react-icons/hi2";
import TablePageLayout from "components/TablePageLayout";
import Pagination from "components/Pagination";
import CustomButton from "components/CustomButton";
import { InputField, SelectField } from "components/FormFields";
import {
  getVisitorsByCompany,
  getVisitorAnalyticsByCompany,
  sendBadgeReminder,
  sendUnpaidReminder,
  updateVisitorDetails,
  toggleVisitorConfirmation,
} from "api/visitor.api";
import Toast from "components/Toast";
import { useCommonLoader } from "components/LoaderContext";
import ConfirmModal from "components/common/ConfirmModal";
import { getCouponById } from "api/coupon.api";
import VisitorEditModal from "components/visitor/VisitorEditModal";

/* ==========================================================================
   1. VISITOR TABLE COMPONENT (INLINE)
   ========================================================================== */
export function VisitorTable({
  data,
  selectedRows = [],
  onSelectRow,
  onSelectAll,
  onEdit,
  onSendIndividualReminder,
  onSendIndividualUnpaidReminder,
  sortOrder,
  onSortChange,
  onRequestConfirmToggle,
}) {
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggleMenu = (e, rowId) => {
    e.stopPropagation();
    if (openDropdownId === rowId) {
      setOpenDropdownId(null);
    } else {
      const buttonRect = e.currentTarget.getBoundingClientRect();
      const spaceBelow = window.innerHeight - buttonRect.bottom;
      setOpenUpward(spaceBelow < 200);
      setOpenDropdownId(rowId);
    }
  };

  return (
    <div className="table-scroll-body">
      <table className="custom-table">
        <thead className="custom-table-header text-gray-800">
          <tr>
            <th className="w-12 text-center">
              <input
                type="checkbox"
                className="table-checkbox"
                onChange={onSelectAll}
                checked={data.length > 0 && selectedRows.length === data.length}
              />
            </th>
            <th>Visitor Name</th>
            <th>Contact</th>
            <th>Company</th>

            {/* --- CONFIRMED COLUMN (NEARBY COMPANY, NO BADGE) --- */}
            <th className="text-center">Confirmed</th>

            <th>Ticket Code</th>

            {/* --- SORTABLE PURCHASE DATE HEADER --- */}
            <th className="select-none">
              <div
                className="inline-flex cursor-pointer items-center gap-2"
                onClick={onSortChange}
              >
                <span>Purchase Date</span>
                {sortOrder === "desc" ? (
                  <MdArrowDownward className="text-base text-[#1a1a1a] transition-colors hover:text-blue-600" />
                ) : (
                  <MdArrowUpward className="text-base text-[#1a1a1a] transition-colors hover:text-blue-600" />
                )}
              </div>
            </th>

            <th>Coupon</th>
            <th>Amount</th>
            <th>Bought By</th>
            <th className="text-center">Payment Status</th>
            <th className="sticky right-0 z-20 w-16 bg-gray-100 text-center shadow-[-2px_0_5px_rgba(0,0,0,0.05)]"></th>
          </tr>
        </thead>

        <tbody>
          {data.length === 0 ? (
            <tr>
              <td
                colSpan="12"
                className="custom-table-cell py-12 text-center text-gray-500"
              >
                No visitors found
              </td>
            </tr>
          ) : (
            data.map((item, index) => {
              const rowId = item._id || index;
              const isChecked = selectedRows.includes(rowId);
              const isMenuOpen = openDropdownId === rowId;
              const isConfirmed = Boolean(item.isConfirmed);

              return (
                <tr key={rowId} className="custom-table-row">
                  <td className="custom-table-cell text-center">
                    <input
                      type="checkbox"
                      className="table-checkbox"
                      checked={isChecked}
                      onChange={() => onSelectRow(rowId)}
                    />
                  </td>

                  <td className="custom-table-cell font-bold text-gray-800">
                    {item.fullName || "N/A"}
                  </td>

                  <td className="custom-table-cell">
                    <div className="flex flex-col">
                      <span className="no-cap font-medium text-gray-700">
                        {item.email || "N/A"}
                      </span>
                      <span className="text-xs text-gray-400">
                        {item.phoneNumber || "N/A"}
                      </span>
                    </div>
                  </td>

                  <td className="custom-table-cell text-gray-700">
                    {item.companyName || "N/A"}
                  </td>

                  {/* Plain text without badge */}
                  <td className="custom-table-cell text-center font-medium text-gray-700">
                    {isConfirmed ? "Yes" : "No"}
                  </td>

                  <td className="custom-table-cell font-medium text-gray-700">
                    {item.tickets?.[0]?.ticketCode || "N/A"}
                  </td>

                  <td className="custom-table-cell text-gray-700">
                    {item.tickets?.[0]?.createdAt
                      ? new Date(item.tickets[0].createdAt).toLocaleString(
                          "en-GB",
                          {
                            day: "2-digit",
                            month: "2-digit",
                            year: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          }
                        )
                      : "N/A"}
                  </td>

                  <td className="custom-table-cell text-center">
                    {item.tickets?.[0]?.order?.couponName || "N/A"}
                  </td>

                  <td className="custom-table-cell text-center">
                    {item.hasSuccessfulPayment
                      ? item.tickets?.[0]?.order?.totalAmount || "0.00"
                      : "0.00"}
                  </td>

                  <td className="custom-table-cell text-center">
                    {item.boughtBy?.[0]?.fullName || "N/A"}
                  </td>

                  <td className="custom-table-cell text-center">
                    <span
                      className={`inline-block rounded-[8px] px-3 py-1 text-xs font-semibold ${
                        item.hasSuccessfulPayment
                          ? "bg-[#e6f9ed] text-[#22c55e]"
                          : "bg-amber-50 text-amber-600"
                      }`}
                    >
                      {item.hasSuccessfulPayment ? "Paid" : "Pending"}
                    </span>
                  </td>

                  {/* Action Dropdown Cell */}
                  <td className="custom-table-cell relative sticky right-0 z-20 w-16 bg-gray-100 text-center shadow-[-2px_0_5px_rgba(0,0,0,0.05)]">
                    <button
                      onClick={(e) => handleToggleMenu(e, rowId)}
                      className="rounded-lg border border-gray-200 bg-white p-1.5 transition-colors hover:bg-gray-50"
                    >
                      <MdMoreVert className="text-lg text-gray-800" />
                    </button>

                    {isMenuOpen && (
                      <div
                        ref={dropdownRef}
                        style={{
                          boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.13)",
                        }}
                        className={`absolute right-full z-[9999] mr-2 flex w-max min-w-[195px] flex-col gap-1 rounded-xl border border-gray-100 bg-white p-2 text-left shadow-lg ${
                          openUpward ? "bottom-0" : "top-0"
                        }`}
                      >
                        {/* Edit Item */}
                        <button
                          onClick={() => {
                            setOpenDropdownId(null);
                            if (onEdit) onEdit(item);
                          }}
                          className="action-dropdown-item flex items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-blue-600"
                        >
                          <MdEdit className="text-base text-gray-500" />
                          Edit
                        </button>

                        <div className="action-dropdown-divider my-1 border-t border-gray-100" />

                        {/* Confirm / Revoke Item */}
                        {isConfirmed ? (
                          <button
                            onClick={() => {
                              setOpenDropdownId(null);
                              if (onRequestConfirmToggle)
                                onRequestConfirmToggle(item);
                            }}
                            className="action-dropdown-item hover:bg-rose-50 hover:text-rose-600 flex items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium text-gray-700 transition-colors"
                          >
                            <MdHighlightOff className="text-rose-500 text-base" />
                            Revoke Confirm
                          </button>
                        ) : (
                          <button
                            onClick={() => {
                              setOpenDropdownId(null);
                              if (onRequestConfirmToggle)
                                onRequestConfirmToggle(item);
                            }}
                            className="action-dropdown-item hover:bg-emerald-50 hover:text-emerald-600 flex items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium text-gray-700 transition-colors"
                          >
                            <MdCheckCircle className="text-emerald-500 text-base" />
                            Confirm Visitor
                          </button>
                        )}

                        <div className="action-dropdown-divider my-1 border-t border-gray-100" />

                        {/* Reminder Items */}
                        {item.hasSuccessfulPayment ? (
                          <button
                            onClick={() => {
                              setOpenDropdownId(null);
                              if (onSendIndividualReminder) {
                                onSendIndividualReminder(item._id);
                              }
                            }}
                            className="action-dropdown-item flex items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-blue-600"
                          >
                            <MdMarkEmailRead className="text-base text-blue-500" />
                            Send Reminder
                          </button>
                        ) : (
                          <button
                            onClick={() => {
                              setOpenDropdownId(null);
                              if (onSendIndividualUnpaidReminder) {
                                onSendIndividualUnpaidReminder(item._id);
                              }
                            }}
                            className="action-dropdown-item flex items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-amber-600"
                          >
                            <MdMarkEmailRead className="text-base text-amber-500" />
                            Send Payment Reminder
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
/* ==========================================================================
   2. MAIN VISITORS PAGE COMPONENT
   ========================================================================== */
export default function Visitors() {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sendingReminder, setSendingReminder] = useState(false);

  // Sorting
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  const { showLoader, hideLoader } = useCommonLoader();
  const [toast, setToast] = useState({
    open: false,
    title: "",
    message: "",
    type: "success",
  });

  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [editingVisitor, setEditingVisitor] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [updatingVisitor, setUpdatingVisitor] = useState(false);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [couponFilter, setCouponFilter] = useState("all");
  const [couponOptions, setCouponOptions] = useState([]);

  const [paginationInfo, setPaginationInfo] = useState({
    totalVisitors: 0,
    totalPages: 1,
  });

  const [analytics, setAnalytics] = useState({
    totalVisitorsCount: 0,
    paidTicketCount: 0,
    unpaidTicketCount: 0,
  });

  const [openConfirmModal, setOpenConfirmModal] = useState(false);

  // Status Confirmation Modal State
  const [targetVisitorForStatus, setTargetVisitorForStatus] = useState(null);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const [selectedOrganizer, setSelectedOrganizer] = useState(
    localStorage.getItem("company_id") || ""
  );

  const abortControllerRef = useRef(null);

  const handleSortChange = () => {
    setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
    setPage(1);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  const fetchAnalytics = useCallback(async () => {
    if (!selectedOrganizer) return;
    try {
      const res = await getVisitorAnalyticsByCompany(selectedOrganizer);
      if (res.data?.data) {
        setAnalytics(res.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    }
  }, [selectedOrganizer]);

  const fetchData = useCallback(async () => {
    if (!selectedOrganizer) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setLoading(true);
      const res = await getVisitorsByCompany(
        selectedOrganizer,
        {
          page,
          limit,
          search: debouncedSearch,
          status: statusFilter,
          coupon: couponFilter,
          startDate,
          endDate,
          sortBy,
          sortOrder,
        },
        { signal: controller.signal }
      );

      const payload = res.data?.data || {};
      setData(payload.visitors || []);
      setPaginationInfo({
        totalVisitors: payload.pagination?.totalVisitors || 0,
        totalPages: payload.pagination?.totalPages || 1,
      });
    } catch (error) {
      if (error.name === "CanceledError" || error.name === "AbortError") return;
      console.error("Failed to fetch visitors:", error);
    } finally {
      setLoading(false);
    }
  }, [
    selectedOrganizer,
    page,
    limit,
    debouncedSearch,
    statusFilter,
    startDate,
    endDate,
    couponFilter,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  useEffect(() => {
    const fetchCoupons = async () => {
      if (!selectedOrganizer) return;
      try {
        const res = await getCouponById(selectedOrganizer);
        if (res.data?.data) {
          setCouponOptions(res.data.data);
        } else {
          setCouponOptions([]);
        }
      } catch (error) {
        console.error("Failed to fetch coupons:", error);
        setCouponOptions([]);
      }
    };

    fetchCoupons();
    const handleOrganizerChange = (e) => {
      setSelectedOrganizer(e.detail);
      setPage(1);
    };
    window.addEventListener("companyChanged", handleOrganizerChange);
    return () =>
      window.removeEventListener("companyChanged", handleOrganizerChange);
  }, [selectedOrganizer]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(data.map((item, idx) => item._id || idx));
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleOpenView = (visitorItem) => {
    setSelectedVisitor(visitorItem);
    setIsViewOpen(true);
  };

  const handleStatusCardClick = (type) => {
    setStatusFilter(type);
    setPage(1);
  };

  const formatDateString = (date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const handleDateRangeChange = (value) => {
    setDateRange(value);
    const today = new Date();
    const todayStr = formatDateString(today);

    if (value === "today") {
      setStartDate(todayStr);
      setEndDate(todayStr);
    } else if (value === "7days") {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(today.getDate() - 7);
      setStartDate(formatDateString(sevenDaysAgo));
      setEndDate(todayStr);
    } else if (value === "30days") {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(today.getDate() - 30);
      setStartDate(formatDateString(thirtyDaysAgo));
      setEndDate(todayStr);
    } else {
      setStartDate("");
      setEndDate("");
    }
    setPage(1);
  };

  // Open confirmation modal for status toggle
  const handleRequestStatusChange = (visitor) => {
    setTargetVisitorForStatus(visitor);
    setStatusModalOpen(true);
  };

  // Call API and update local data directly without refetching the entire table
  const handleConfirmStatusToggle = async () => {
    if (!targetVisitorForStatus) return;

    const newStatus = !targetVisitorForStatus.isConfirmed;
    const visitorId = targetVisitorForStatus._id;

    try {
      setUpdatingStatus(true);
      await toggleVisitorConfirmation(visitorId, newStatus);

      // Local State Update: mutate only this row
      setData((prevData) =>
        prevData.map((item) =>
          item._id === visitorId ? { ...item, isConfirmed: newStatus } : item
        )
      );

      setToast({
        open: true,
        title: "Success",
        message: `Visitor marked as ${
          newStatus ? "Confirmed" : "Unconfirmed"
        }.`,
        type: "success",
      });

      setStatusModalOpen(false);
      setTargetVisitorForStatus(null);
    } catch (error) {
      console.error("Failed to update confirmation status:", error);
      setToast({
        open: true,
        title: "Error",
        message: error.response?.data?.message || "Failed to update status.",
        type: "error",
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleExportCSV = async () => {
    let exportData = [];

    if (selectedRows.length > 0) {
      exportData = data.filter((item, idx) =>
        selectedRows.includes(item._id || idx)
      );
    } else {
      try {
        showLoader("Download visitor records...");
        const res = await getVisitorsByCompany(selectedOrganizer, {
          page: 1,
          limit: 100000,
          search: debouncedSearch,
          status: statusFilter,
          startDate,
          endDate,
          sortBy,
          sortOrder,
        });
        exportData = res.data?.data?.visitors || [];
      } catch (err) {
        console.error("Failed to fetch all visitors for CSV export:", err);
        exportData = data;
      }
    }

    if (!exportData.length) return;

    const formatCellValue = (val) => {
      if (val === null || val === undefined) return '""';
      const strVal = String(val).replace(/"/g, '""');
      return `"${strVal}"`;
    };

    const headers = [
      "Visitor ID",
      "First Name",
      "Last Name",
      "Email",
      "Country Code",
      "Phone Number",
      "Company Name",
      "Confirmed",
      "Job Title",
      "Visitor Created At",
      "County",
      "City",
      "Company Type",
      "Invoice Type",
      "Invoice First Name",
      "Invoice Last Name",
      "Invoice Email",
      "Invoice Country Code",
      "Invoice Mobile Number",
      "Invoice CNP",
      "Invoice Company Name",
      "Invoice Address",
      "Invoice City",
      "Invoice County",
      "Invoice Country",
      "Invoice CUI",
      "Invoice Trade Register No",
      "Ticket Code",
      "Ticket Price",
      "Coupon Name",
      "Payment Status",
    ];

    const rows = exportData.map((item) => {
      const inv = item.invoice || {};
      const oth = item.otherDetails || {};
      const ticket = item.tickets?.[0] || {};
      const order = ticket.order || {};

      return [
        formatCellValue(item._id),
        formatCellValue(item.firstName),
        formatCellValue(item.lastName),
        formatCellValue(item.email),
        formatCellValue(item.countryCode),
        formatCellValue(item.phoneNumber),
        formatCellValue(item.companyName),
        formatCellValue(item.isConfirmed ? "Yes" : "No"),
        formatCellValue(item.jobTitle),
        formatCellValue(item.visitorCreatedAt || item.createdAt),
        formatCellValue(oth.county),
        formatCellValue(oth.city),
        formatCellValue(oth.companyType),
        formatCellValue(inv.type),
        formatCellValue(inv.firstName),
        formatCellValue(inv.lastName),
        formatCellValue(inv.email),
        formatCellValue(inv.countryCode),
        formatCellValue(inv.mobileNumber),
        formatCellValue(inv.cnp),
        formatCellValue(inv.companyName),
        formatCellValue(inv.address),
        formatCellValue(inv.city),
        formatCellValue(inv.county),
        formatCellValue(inv.country),
        formatCellValue(inv.cui),
        formatCellValue(inv.tradeRegisterNo),
        formatCellValue(ticket.ticketCode),
        formatCellValue(order.totalAmount),
        formatCellValue(order.couponName),
        formatCellValue(item.hasSuccessfulPayment ? "Paid" : "Pending"),
      ];
    });

    const csvContent =
      "\uFEFF" +
      [headers.join(","), ...rows.map((row) => row.join(","))].join("\r\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `visitors_${
        selectedRows.length > 0 ? "selected" : statusFilter
      }_${Date.now()}.csv`
    );

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    hideLoader();
  };

  const handleSendIndividualReminder = async (visitorId) => {
    try {
      showLoader("Send email reminder for individual...");
      await sendBadgeReminder({
        companyId: selectedOrganizer,
        type: "individual",
        visitorId,
      });

      setToast({
        open: true,
        title: "Reminder Sent!",
        message: "Badge reminder email dispatched successfully.",
        type: "success",
      });
    } catch (error) {
      console.error("Failed to send reminder:", error);
      setToast({
        open: true,
        title: "Error Sending Reminder",
        message:
          error.response?.data?.message ||
          "Failed to send badge reminder email.",
        type: "error",
      });
    } finally {
      hideLoader();
    }
  };

  const handleSendAllPaidReminders = async () => {
    try {
      setSendingReminder(true);
      const res = await sendBadgeReminder({
        companyId: selectedOrganizer,
        type: "all",
      });

      setToast({
        open: true,
        title: "Bulk Reminders Dispatched!",
        message:
          res.message ||
          "Badge reminder emails sent to all paid visitors successfully.",
        type: "success",
      });
    } catch (error) {
      console.error("Failed to send bulk reminders:", error);
      setToast({
        open: true,
        title: "Error Sending Reminders",
        message:
          error.response?.data?.message || "Failed to send reminder emails.",
        type: "error",
      });
    } finally {
      setSendingReminder(false);
    }
  };

  const handleSendIndividualUnpaidReminder = async (visitorId) => {
    try {
      showLoader("Sending payment reminder email...");
      await sendUnpaidReminder({
        companyId: selectedOrganizer,
        type: "individual",
        visitorId,
      });

      setToast({
        open: true,
        title: "Reminder Sent!",
        message: "Payment reminder email dispatched successfully.",
        type: "success",
      });
    } catch (error) {
      console.error("Failed to send unpaid reminder:", error);
      setToast({
        open: true,
        title: "Error Sending Reminder",
        message:
          error.response?.data?.message ||
          "Failed to send payment reminder email.",
        type: "error",
      });
    } finally {
      hideLoader();
    }
  };

  const handleClearAllFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setDateRange("all");
    setCouponFilter("all");
    setStartDate("");
    setEndDate("");
    setSortBy("createdAt");
    setSortOrder("desc");
    setPage(1);
  };

  const activeFilterCount = [
    statusFilter !== "all",
    dateRange !== "all",
    couponFilter !== "all",
  ].filter(Boolean).length;

  const handleUpdateVisitor = async (formData) => {
    try {
      setUpdatingVisitor(true);
      await updateVisitorDetails(editingVisitor._id, formData);

      setToast({
        open: true,
        title: "Success",
        message: "Visitor details updated successfully.",
        type: "success",
      });

      setIsEditModalOpen(false);
      setEditingVisitor(null);
      fetchData();
    } catch (error) {
      console.error("Failed to update visitor:", error);
      setToast({
        open: true,
        title: "Update Failed",
        message: error.response?.data?.message || "Failed to update visitor.",
        type: "error",
      });
    } finally {
      setUpdatingVisitor(false);
    }
  };

  const visitorCards = [
    {
      label: "Total Visitors",
      value: analytics.totalVisitorsCount || "0",
      icon: <MdPeople className="text-lg" />,
      bgClass: "bg-blue-50 text-blue-500",
      onClick: () => handleStatusCardClick("all"),
      active: statusFilter === "all",
    },
    {
      label: "Paid Tickets",
      value: analytics.paidVisitorsCount || "0",
      icon: <HiOutlineTicket className="text-lg" />,
      bgClass: "bg-green-50 text-green-500",
      onClick: () => handleStatusCardClick("paid"),
      active: statusFilter === "paid",
    },
    {
      label: "Pending",
      value: analytics.unpaidVisitorsCount || "0",
      icon: <MdErrorOutline className="text-lg" />,
      bgClass: "bg-amber-50 text-amber-500",
      onClick: () => handleStatusCardClick("unpaid"),
      active: statusFilter === "unpaid",
    },
  ];

  return (
    <TablePageLayout
      subtitle="Manage and view all visitor records and export data"
      cards={visitorCards}
    >
      <Toast
        open={toast.open}
        title={toast.title}
        message={toast.message}
        type={toast.type}
        duration={1500}
        onClose={() => {
          setToast((prev) => ({ ...prev, open: false }));
        }}
      />

      <div className="grid w-full grid-cols-1 items-end gap-3 lg:grid-cols-3">
        <InputField
          label="SEARCH"
          name="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search..."
          icon={<MdSearch className="text-lg text-gray-400" />}
        />
        <div></div>
        <div className="flex items-center justify-start gap-2 sm:justify-end lg:justify-end">
          <CustomButton
            label={sendingReminder ? "Sending..." : "Notify All"}
            icon={MdMarkEmailRead}
            iconPos="left"
            variant="primary"
            disabled={sendingReminder || !selectedOrganizer}
            onClick={() => setOpenConfirmModal(true)}
          />
          <CustomButton
            label={
              selectedRows.length > 0
                ? `Export Selected (${selectedRows.length})`
                : "Export CSV"
            }
            icon={MdDownload}
            iconPos="left"
            variant="outline"
            color="#1A1A1A"
            borderColor="#02a9e0"
            disabled={data.length === 0}
            onClick={handleExportCSV}
          />

          <CustomButton
            label={"Filters"}
            icon={isFilterOpen ? MdKeyboardArrowUp : MdKeyboardArrowDown}
            iconPos="right"
            variant="outline"
            color="#1A1A1A"
            borderColor="#02a9e0"
            onClick={() => setIsFilterOpen((prev) => !prev)}
          />
        </div>
      </div>

      {(isFilterOpen || activeFilterCount > 0) && (
        <div className="rounded-2xl border border-gray-100 bg-gray-50/70 p-5 shadow-sm transition-all">
          {activeFilterCount > 0 && (
            <div
              className={`flex flex-wrap items-center gap-2 ${
                isFilterOpen ? "mb-4 border-b border-gray-200/60 pb-3" : ""
              }`}
            >
              {statusFilter !== "all" && (
                <span className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-700 shadow-sm">
                  Status: <strong className="capitalize">{statusFilter}</strong>
                  <button
                    onClick={() => setStatusFilter("all")}
                    className="hover:text-red-500"
                  >
                    <MdClose />
                  </button>
                </span>
              )}

              {dateRange !== "all" && (
                <span className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-700 shadow-sm">
                  Date: <strong className="capitalize">{dateRange}</strong>
                  <button
                    onClick={() => handleDateRangeChange("all")}
                    className="hover:text-red-500"
                  >
                    <MdClose />
                  </button>
                </span>
              )}

              {couponFilter !== "all" && (
                <span className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-700 shadow-sm">
                  Coupon:{" "}
                  <strong>
                    {couponFilter === "nocoupon"
                      ? "Without Coupon"
                      : couponOptions.find((c) => c._id === couponFilter)
                          ?.couponName || "Selected"}
                  </strong>
                  <button
                    onClick={() => setCouponFilter("all")}
                    className="hover:text-red-500"
                  >
                    <MdClose />
                  </button>
                </span>
              )}
            </div>
          )}

          {isFilterOpen && (
            <div className="grid grid-cols-1 items-end gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <SelectField
                label="STATUS"
                name="statusFilter"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                options={[
                  { label: "All Statuses", value: "all" },
                  { label: "Paid", value: "paid" },
                  { label: "Pending", value: "unpaid" },
                ]}
              />

              <SelectField
                label="DATE RANGE"
                name="dateRange"
                value={dateRange}
                onChange={(e) => handleDateRangeChange(e.target.value)}
                options={[
                  { label: "All Dates", value: "all" },
                  { label: "Today", value: "today" },
                  { label: "Last 7 Days", value: "7days" },
                  { label: "Last 30 Days", value: "30days" },
                ]}
              />

              <SelectField
                label="COUPON"
                name="couponFilter"
                value={couponFilter}
                onChange={(e) => {
                  setCouponFilter(e.target.value);
                  setPage(1);
                }}
                options={[
                  { label: "All Coupons", value: "all" },
                  { label: "Without Coupon", value: "nocoupon" },
                  ...couponOptions.map((cp) => ({
                    label: cp.couponName,
                    value: cp._id,
                  })),
                ]}
              />

              <div>
                <CustomButton
                  label={" Clear All"}
                  variant="outline"
                  color="#1A1A1A"
                  borderColor="#02a9e0"
                  onClick={handleClearAllFilters}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="mt-4 rounded-xl border bg-white p-12 text-center text-sm text-gray-500 shadow-sm">
          Loading visitors...
        </div>
      ) : (
        <div className="table-card mt-4 overflow-hidden">
          <VisitorTable
            data={data}
            selectedRows={selectedRows}
            onSelectRow={handleSelectRow}
            onSelectAll={handleSelectAll}
            viewDetails={handleOpenView}
            onEdit={(visitor) => {
              setEditingVisitor(visitor);
              setIsEditModalOpen(true);
            }}
            onSendIndividualReminder={handleSendIndividualReminder}
            onSendIndividualUnpaidReminder={handleSendIndividualUnpaidReminder}
            sortOrder={sortOrder}
            onSortChange={handleSortChange}
            onRequestConfirmToggle={handleRequestStatusChange}
          />

          <Pagination
            currentPage={page}
            totalPages={paginationInfo.totalPages}
            totalEntries={paginationInfo.totalVisitors}
            limit={limit}
            onPageChange={(newPage) => setPage(newPage)}
            onLimitChange={(newLimit) => setLimit(newLimit)}
          />
        </div>
      )}

      {/* --- CONFIRM/REVOKE VISITOR STATUS MODAL --- */}
      <ConfirmModal
        open={statusModalOpen}
        title={
          targetVisitorForStatus?.isConfirmed
            ? "Revoke Visitor Confirmation"
            : "Confirm Visitor Registration"
        }
        message={
          targetVisitorForStatus?.isConfirmed
            ? `Are you sure you want to revoke confirmation for "${targetVisitorForStatus?.fullName}"?`
            : `Are you sure you want to mark "${targetVisitorForStatus?.fullName}" as confirmed?`
        }
        confirmText={
          targetVisitorForStatus?.isConfirmed
            ? "Revoke Confirmation"
            : "Confirm"
        }
        loading={updatingStatus}
        icon={
          targetVisitorForStatus?.isConfirmed ? MdHighlightOff : MdCheckCircle
        }
        iconBgClass={
          targetVisitorForStatus?.isConfirmed
            ? "bg-rose-50 text-rose-600"
            : "bg-emerald-50 text-emerald-600"
        }
        confirmBtnBg={
          targetVisitorForStatus?.isConfirmed ? "#E11D48" : "#2563EB"
        }
        onConfirm={handleConfirmStatusToggle}
        onCancel={() => {
          setStatusModalOpen(false);
          setTargetVisitorForStatus(null);
        }}
      />

      <ConfirmModal
        open={openConfirmModal}
        title="Notify All Visitors"
        message="Would you like to resend the ticket confirmation email to all participants?"
        confirmText="Notify"
        loading={loading}
        icon={MdMarkEmailRead}
        iconBgClass="bg-blue-50 text-blue-600"
        confirmBtnBg="#2563EB"
        onConfirm={handleSendAllPaidReminders}
        onCancel={() => setOpenConfirmModal(false)}
      />

      <VisitorEditModal
        open={isEditModalOpen}
        initialData={editingVisitor}
        loading={updatingVisitor}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingVisitor(null);
        }}
        onSubmit={handleUpdateVisitor}
      />
    </TablePageLayout>
  );
}
